import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Award, Users, Globe, Shield, Clock, Quote } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Safety First',
      description: 'Every flight operates under the highest safety standards with thoroughly vetted operators and aircraft.',
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Excellence',
      description: 'We maintain unwavering commitment to service excellence in every aspect of your aviation experience.',
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Reliability',
      description: '24/7 availability with rapid response times and dependable service delivery worldwide.',
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: 'Global Reach',
      description: 'Extensive network of trusted partners and aircraft operators spanning every continent.',
    },
  ];

  const milestones = [
    { year: '2010', title: 'Founded in Singapore', description: 'Established by veteran airline captains and charter specialists with a vision for Singapore centric premium aviation services.' },
    { year: '2012', title: 'Expanding Client Base', description: 'First charter flight for head of state utilising Airbus A320 and Boeing B777 aircraft.' },
    { year: '2013', title: 'Singapore Government Becomes a Client', description: "Singapore Air Charter provides an Embraer aircraft for Singapore President Dr Tony Tan and Mrs Tan's official state visit to Myanmar." },
    { year: '2016', title: 'Royalty on Board', description: 'Singapore Air Charter provides bespoke air charter intra-Asia services for a prominent royal family member.' },
    { year: '2019', title: 'On-Demand Fleet Network Expansion', description: 'Singapore Air Charter teams up with global aviation behemoth to expand its access to 500+ aircraft worldwide through strategic partnership.' },
    { year: '2025', title: 'Happy Birthday!', description: 'Singapore Air Charter celebrates fifteen years of providing bespoke air charter services to its esteemed local and global clientele.' },
  ];

const testimonials = [
  { text: "We have had very positive feedback from staff and clients on the charter experience. I know this project probably turned out bigger than you had expected so I wanted to say thank you for the flexibility and efforts. I also appreciate your persistence in getting things sorted for us.", author: "Head of Hospitality and Events" },
  { text: "Thank you very much for all the planes you organized so well. Can you we please kindly ask you for your address so that we can send you a thank you letter from the Prince.", author: "Secretary to H.S.H. Prince" },
  { text: "The trip was fantastic. My BOD were so happy and very satisfied. The food was great. I loved it.", author: "Executive Assistant" },
  { text: "Thank you for staying on top of it, it was delivered and all is well. I appreciate the effort.", author: "Logistics Client" },
  { text: "Once again you and the team's services have been excellent. In the near future we will try to give you more notice with flights that we require!", author: "Project Engineer" },

  // ✅ 修正 + 加 author
  { text: "Great news! Certainly enjoyed your services. Many thanks to you, great cooperation and organization, it all went very smoothly.", author: "PA to H.S.H. The Prince" },

  { text: "He said that from start to finish the service was impeccable. From the hotel location, the transfer to the airport, the helicopter transfer and the return journey also.", author: "Project Manager" },
  { text: "Your support went much much beyond our agreement and I really have to thank you for that. You cannot imagine how important logistics are in this kind of operation!", author: "Diplomatic Advisor to the President" },
  { text: "He really enjoyed the flights and loved the new Falcon. The pilots were great as were all of the ground staff. I will definitely be contacting you again for any charter needs with Asia.", author: "Personal Assistant, California USA" },

  // ✅ grammar 修正
  { text: "Thanks again for all your help and the excellent quick service that you provided to us that helped us so much.", author: "Projects Manager, UK Shipping" },

  // ✅ 保留（客户要求）
  { text: "I just wanted to pass on our thanks for all your help recently and for doing the running around to ensure our deliveries were collected, that was certainly above and beyond what we could expect.", author: "Project Manager" },

  // ✅ 修正语法 + 正确格式
  { 
    text: "They are the dynamic duo behind the scenes, putting the comfort of our 54 VIP customers as top priority, while executing everything to the tee. Even with last-minute curveballs, they handled everything with grace and precision. Big thanks to you and SAC. Highly recommended.", 
    author: "Client" 
  },
];

  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/a01.jpg"
            alt="Private jet on tarmac"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
        <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
  ABOUT US
</span>
            <h1 
              data-testid="about-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Aviation Expertise Built on Experience
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl">
              Singapore Air Charter provides tailored aviation solutions across private charter, group charter, cargo and special operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="caption-text text-blue-700 mb-4 block">Our Story</span>
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Trusted Aviation Partner Since 2010
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                Singapore Air Charter was founded in 2010 by a management team of Singapore based aviation professionals who hated 'one-size-fits-all' aviation.
              </p>
              <p className="body-default mb-6">
                Their background encompassed many facets of commercial aviation from airline flying, including management and training positions to starting, owning and managing a large overseas charter operation with more than forty aircraft. Drawing on previous experience with a varied client base including government ministers, dignitaries and NGO's, it was with aircraft charter that they knew they could better serve the Singapore and wider market.
              </p>
              <p className="body-default mb-6">
                Fast forward to today and Singapore Air Charter is still independent, still obsessive about detail and still run by the same founding management team who combined offer more than 70 years of aviation experience. 
              </p>
              <p className="body-default mb-8 font-medium text-slate-800">
                From royalty, heads-of-state and large corporations to private individuals who prefer the comfort and convenience of private air travel, all have put their trust in the team at Singapore Air Charter.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-50 p-6">
                  <span className="text-3xl font-semibold text-[#1e40af]" style={{ fontFamily: 'Playfair Display, serif' }}>70+</span>
                  <p className="text-sm text-slate-600 mt-1">Years of Experience</p>
                </div>
                <div className="bg-slate-50 p-6">
                  <span className="text-3xl font-semibold text-[#1e40af]" style={{ fontFamily: 'Playfair Display, serif' }}>500+</span>
                  <p className="text-sm text-slate-600 mt-1">Aircraft Worldwide</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="/a02.jpg"
                alt="Private jet cockpit"
                className="w-full h-[600px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">Our Values</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              What Drives Us
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`value-card-${index}`}
                className="bg-white p-8 border border-slate-100 text-center"
              >
                <div className="text-blue-700 mb-6 flex justify-center">
                  {value.icon}
                </div>
                <h3 
                  className="text-xl font-medium text-slate-900 mb-3"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-gap">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">Testimonials</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              What Our Clients Say
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-slate-50 p-6 border-l-4 border-blue-700"
              >
                <Quote className="w-6 h-6 text-blue-700 mb-4" />
                <p className="text-slate-600 text-sm leading-relaxed mb-4 italic">
                  "{testimonial.text}"
                </p>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                  — {testimonial.author}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-gap bg-slate-900">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-blue-700 mb-4 block">
              Our Journey
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium text-white mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Milestones
            </h2>
          </motion.div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`milestone-${index}`}
                className="flex flex-col md:flex-row items-start gap-6 border-l-2 border-blue-700/30 pl-6"
              >
                <span 
                  className="text-4xl font-semibold text-blue-700"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {milestone.year}
                </span>
                <div>
                  <h3 className="text-xl font-medium text-white mb-2">{milestone.title}</h3>
                  <p className="text-slate-400 text-sm">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured In Section */}
      <section className="py-16 bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="caption-text text-blue-700 mb-4 block">As Featured In</span>
          </motion.div>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 lg:gap-16">
            {[
              { name: 'JETGALA', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/39jbaqae_Jetgala.jpg' },
              { name: 'CNA', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/zg1s6mai_CNA.jpg' },
              { name: 'Robb Report', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/kzhrq9o4_Robb%20Report%20.jpg' },
              { name: 'Singapore Airshow', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/oip6f75j_Singapore%20Airshow.jpg' },
              { name: 'The Business Times', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/z0q7yvjj_The%20Business%20Time.jpg' },
              { name: 'AIN', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/vcvxrljx_AIN.jpg' },
              { name: 'MillionaireAsia', src: 'https://customer-assets.emergentagent.com/job_singapore-air-v2/artifacts/cl0ooigq_MillionaireAsia.jpg' },
            ].map((logo, index) => (
              <motion.div
                key={logo.name}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="h-10 md:h-12 hover:scale-105 transition-all duration-300"
              >
                <img 
                  src={logo.src} 
                  alt={logo.name}
                  className="h-full w-auto object-contain"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-gap">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="heading-lg text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Experience the Difference
            </h2>
            <p className="body-large mb-10 max-w-2xl mx-auto">
              Discover how Singapore Air Charter can elevate your aviation experience.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              Contact Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
